import React, { useState } from 'react';
import Box from '@mui/material/Box';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { DrawerV2, ButtonV2, ChipV2, dsV2 } from '@bloomays-lib/ui.shared';
import type { BrowserUploadFile, ISociety } from '@bloomays-lib/types.shared';
import { UPSERT_BLOOMER_SOCIETY, UPSERT_BLOOMER } from '@bloomays-lib/adapter.api-bloomer';

import { cancel, notify } from '../../../../helpers/toastify';
import { deriveDocumentStatus, type DocumentStatus } from '../../../../helpers/missionDashboardV2';
import { Upload } from '../../../molecules/Upload';
import { FieldV2, InputV2, InfoV2 } from '../formFields';
import { DocPreviewModal } from './DocPreviewModal';

type DocContract = { type?: string; status?: string; signedDate?: string; signedFileUrl?: string };

export type DocumentsDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  bloomerSociety?: ISociety;
  bloomer?: { recordId?: string; firstname?: string; lastname?: string; email?: string; credentialID?: string };
  contracts?: DocContract[];
};

const STATUS_TONE: Record<DocumentStatus, { bg: string; fg: string; chip: 'success' | 'warning' | 'error' | 'info' }> =
  {
    valid: { bg: dsV2.color.vertDim, fg: dsV2.color.vert, chip: 'success' },
    warn: { bg: dsV2.color.amberDim, fg: dsV2.color.amber, chip: 'warning' },
    expired: { bg: dsV2.color.rougeDim, fg: dsV2.color.rouge, chip: 'error' },
    missing: { bg: dsV2.color.rougeDim, fg: dsV2.color.rouge, chip: 'error' },
    pending: { bg: dsV2.color.bleuDim, fg: dsV2.color.bleu, chip: 'info' },
  };
const STATUS_LABEL: Record<DocumentStatus, string> = {
  valid: 'Valide',
  warn: 'À vérifier',
  expired: 'Expiré',
  missing: 'Manquant',
  pending: 'En cours',
};

/** Ligne d'upload d'un document (`.docm`) : zone drag-and-drop PDF + date d'expiration + enregistrement. */
const DocUploadRow = ({
  label,
  icon,
  optional,
  currentUrl,
  initialExpiration,
  hasExpiration,
  saving,
  onSave,
  onPreview,
}: {
  label: string;
  icon: React.ReactNode;
  optional?: boolean;
  currentUrl?: string;
  initialExpiration?: string;
  hasExpiration: boolean;
  saving: boolean;
  onSave: (args: { file: BrowserUploadFile | null; expiration?: string }) => void;
  onPreview?: (url: string, title: string) => void;
}) => {
  const [file, setFile] = useState<BrowserUploadFile | null>(null);
  const [expiration, setExpiration] = useState<string>(initialExpiration ?? '');

  const uploaded = Boolean(currentUrl) || Boolean(file);
  const status = deriveDocumentStatus({
    uploaded,
    expirationDate: hasExpiration ? expiration || undefined : undefined,
  });
  const tone = STATUS_TONE[status];
  const dirty = file !== null || (hasExpiration && (expiration || '') !== (initialExpiration ?? ''));

  return (
    <Box
      sx={{
        border: `1px solid ${dsV2.color.border}`,
        borderRadius: dsV2.radius.sm,
        padding: '12px 13px',
        marginBottom: '10px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '7px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: tone.bg,
            color: tone.fg,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: dsV2.color.noir }}>
            {label}
            {optional ? ' (optionnel)' : ''}
          </Box>
          {file?.path ? (
            <Box sx={{ fontSize: 11, color: dsV2.color.t4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {file.path}
            </Box>
          ) : null}
        </Box>
        <ChipV2 label={STATUS_LABEL[status]} tone={tone.chip} size="small" />
      </Box>

      {/* Zone drag-and-drop réutilisable (Dropzone), restreinte au PDF. */}
      <Upload
        id={`docup-${label.replace(/\s+/g, '-').toLowerCase()}`}
        noModal
        autoload={false}
        textLabel="Choisir un PDF"
        placeholder="Glissez un PDF ici, ou cliquez pour sélectionner"
        maxFiles={1}
        maxLength={20000000}
        fileTypes={['.pdf']}
        preloadedFiles={[]}
        handleChange={(accepted) => setFile(accepted[0] ?? null)}
      />

      {currentUrl && onPreview ? (
        <Box
          component="button"
          type="button"
          onClick={() => onPreview(currentUrl, label)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: 12,
            color: dsV2.color.t3,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            marginTop: '4px',
          }}
        >
          <VisibilityRoundedIcon sx={{ fontSize: 15 }} /> Aperçu du document actuel
        </Box>
      ) : null}

      {hasExpiration ? (
        <Box sx={{ marginTop: '10px' }}>
          <FieldV2 label="Date d'expiration">
            <InputV2 type="date" value={expiration} onChange={setExpiration} />
          </FieldV2>
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
        <ButtonV2
          size="small"
          disabled={!dirty || saving}
          loading={saving}
          onClick={() => onSave({ file, expiration })}
        >
          Enregistrer
        </ButtonV2>
      </Box>
    </Box>
  );
};

/** Ligne contrat en lecture seule (PDS/SST) — la signature passe par le parcours contrats. */
const ContractRow = ({
  contract,
  onPreview,
}: {
  contract: DocContract;
  onPreview?: (url: string, title: string) => void;
}) => {
  const signed = Boolean(contract.signedDate || contract.signedFileUrl);
  const tone = signed ? STATUS_TONE.valid : STATUS_TONE.warn;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        border: `1px solid ${dsV2.color.border}`,
        borderRadius: dsV2.radius.sm,
        padding: '11px 13px',
        marginBottom: '10px',
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: '7px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          background: tone.bg,
          color: tone.fg,
        }}
      >
        <DescriptionRoundedIcon sx={{ fontSize: 17 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: dsV2.color.noir }}>Contrat {contract.type}</Box>
        <Box sx={{ fontSize: 11, color: dsV2.color.t4 }}>
          {signed && contract.signedDate
            ? `Signé le ${new Date(contract.signedDate).toLocaleDateString('fr-FR')}`
            : 'En attente de signature'}
        </Box>
      </Box>
      {contract.signedFileUrl && onPreview ? (
        <Box
          component="button"
          type="button"
          onClick={() => onPreview(contract.signedFileUrl as string, `Contrat ${contract.type ?? ''}`.trim())}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: 12,
            color: dsV2.color.bleu,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <VisibilityRoundedIcon sx={{ fontSize: 15 }} /> Voir
        </Box>
      ) : null}
    </Box>
  );
};

/**
 * Gestion des documents en panneau latéral (DrawerV2). Upload / remplacement des attestations de la
 * société du Bloomer (KBIS, URSSAF, RCP) via `UPSERT_BLOOMER_SOCIETY` et de la carte d'identité via
 * `UPSERT_BLOOMER`. Les contrats (PDS / SST) sont affichés en lecture seule — la signature passe par
 * le parcours contrats dédié.
 */
export const DocumentsDrawer = ({
  open,
  onClose,
  onSaved,
  bloomerSociety,
  bloomer,
  contracts,
}: DocumentsDrawerProps) => {
  const { t } = useTranslation(['notify']);
  const [upsertBloomerSociety, { loading: savingSociety }] = useMutation(UPSERT_BLOOMER_SOCIETY);
  const [upsertBloomer, { loading: savingBloomer }] = useMutation(UPSERT_BLOOMER);
  const [savingKind, setSavingKind] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null);
  const handlePreview = (url: string, title: string) => setPreview({ url, title });

  const saveSocietyDoc = async (
    kind: string,
    fileField: string,
    dateField: string,
    { file, expiration }: { file: BrowserUploadFile | null; expiration?: string },
  ) => {
    if (!bloomerSociety?.recordId) return;
    const input: Record<string, unknown> = { recordId: bloomerSociety.recordId };
    if (file) input[fileField] = file;
    if (expiration) input[dateField] = expiration;
    setSavingKind(kind);
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await upsertBloomerSociety({ variables: { input } });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onSaved();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    } finally {
      setSavingKind(null);
    }
  };

  const saveCI = async ({ file }: { file: BrowserUploadFile | null }) => {
    if (!bloomer?.recordId || !file) return;
    setSavingKind('ci');
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await upsertBloomer({
        variables: {
          input: {
            recordId: bloomer.recordId,
            firstname: bloomer.firstname,
            lastname: bloomer.lastname,
            email: bloomer.email,
            credentialID: file,
          },
        },
      });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onSaved();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    } finally {
      setSavingKind(null);
    }
  };

  const pds = contracts?.find((c) => c.type === 'PDS');
  const sst = contracts?.find((c) => c.type === 'SST');

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title="Documents"
      subtitle={bloomerSociety?.name ? `${bloomerSociety.name} · mission` : 'Mission'}
      width={480}
      footer={<ButtonV2 onClick={onClose}>Fermer</ButtonV2>}
    >
      <InfoV2 tone="amber" icon={<ScheduleRoundedIcon sx={{ fontSize: 16 }} />}>
        Contrats (PDS · Client / SST · Bloomer) : la signature se gère depuis le parcours contrats. Attestations :
        ajoute le fichier et sa date d'expiration.
      </InfoV2>

      {pds ? <ContractRow contract={pds} onPreview={handlePreview} /> : null}
      {sst ? <ContractRow contract={sst} onPreview={handlePreview} /> : null}

      <DocUploadRow
        label="Carte d'identité (Bloomer)"
        icon={<BadgeRoundedIcon sx={{ fontSize: 17 }} />}
        currentUrl={bloomer?.credentialID}
        hasExpiration={false}
        saving={savingBloomer && savingKind === 'ci'}
        onSave={saveCI}
        onPreview={handlePreview}
      />
      <DocUploadRow
        label="Extrait KBIS"
        icon={<AccountBalanceRoundedIcon sx={{ fontSize: 17 }} />}
        currentUrl={bloomerSociety?.KBIS}
        initialExpiration={bloomerSociety?.KbisExpirationDate}
        hasExpiration
        saving={savingSociety && savingKind === 'kbis'}
        onSave={(args) => saveSocietyDoc('kbis', 'KBIS', 'KbisExpirationDate', args)}
        onPreview={handlePreview}
      />
      <DocUploadRow
        label="Attestation de vigilance URSSAF"
        icon={<VerifiedRoundedIcon sx={{ fontSize: 17 }} />}
        currentUrl={bloomerSociety?.URSSAFVigilanceCertificate}
        initialExpiration={bloomerSociety?.URSSAFVCExpirationDate}
        hasExpiration
        saving={savingSociety && savingKind === 'urssaf'}
        onSave={(args) => saveSocietyDoc('urssaf', 'URSSAFVigilanceCertificate', 'URSSAFVCExpirationDate', args)}
        onPreview={handlePreview}
      />
      <DocUploadRow
        label="Assurance RCP"
        icon={<ShieldRoundedIcon sx={{ fontSize: 17 }} />}
        optional
        currentUrl={bloomerSociety?.RCPVigilanceCertificate}
        initialExpiration={bloomerSociety?.RCPExpirationDate}
        hasExpiration
        saving={savingSociety && savingKind === 'rcp'}
        onSave={(args) => saveSocietyDoc('rcp', 'RCPVigilanceCertificate', 'RCPExpirationDate', args)}
        onPreview={handlePreview}
      />

      {preview ? (
        <DocPreviewModal open url={preview.url} title={preview.title} onClose={() => setPreview(null)} />
      ) : null}
    </DrawerV2>
  );
};

export default DocumentsDrawer;
